import {
  GroupNameDrawClickEvent,
  GroupNameResetClickEvent,
} from '#src/groups/analytics/group_analytics_events.ts';
import { DeviceMemoryRules } from '#src/groups/device_memory/device_memory.ts';
import type { UseDeviceMemoryResult } from '#src/groups/device_memory/use_device_memory.ts';
import { getGroupQueryKey } from '#src/groups/get_group/use_get_group.ts';
import { useGetGroupForParticipant } from '#src/groups/get_group/use_get_group_for_participant.ts';
import { AffiliateDisclosureCard } from '#src/pages/group/affiliate_disclosure_card/affiliate_disclosure_card.tsx';
import { DeletedGroupCard } from '#src/pages/group/deleted_group_card/deleted_group_card.tsx';
import { DrawCard } from '#src/pages/group/draw_card/draw_card.tsx';
import { DrawSlotMachine } from '#src/pages/group/draw_card/draw_slot_machine.tsx';
import { useRecordMatchView } from '#src/pages/group/draw_card/use_record_match_view.ts';
import { GroupHeaderCard } from '#src/pages/group/group_header_card/group_header_card.tsx';
import { HelpCard } from '#src/pages/group/help_card/help_card.tsx';
import { MatchCard } from '#src/pages/group/match_card/match_card.tsx';
import { MatchWishlistCard } from '#src/pages/group/match_wishlist_card/match_wishlist_card.tsx';
import { OwnWishlistCard } from '#src/pages/group/own_wishlist_card/own_wishlist_card.tsx';
import { ProgressCard } from '#src/pages/group/progress_card/progress_card.tsx';
import { useServices } from '#src/services/services.ts';
import { NotFoundPage } from '#src/ui/error/not_found_page.tsx';
import { UnknownErrorPage } from '#src/ui/error/unknown_error_page.tsx';
import { PageLayout } from '#src/ui/layout/page_layout.tsx';
import { LoadingPage } from '#src/ui/loading/loading_page.tsx';
import { getCreateGroupHref, getGroupHref } from '#src/ui/navigation/navigation.ts';
import { GetGroupForParticipantErrorCodes, GetGroupSuccessKinds } from '@packages/contracts/group';
import { invariant } from '@packages/primitives/invariant';
import { UnreachableError } from '@packages/primitives/unreachable_error';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useState } from 'react';

export interface ParticipantDashboardProps {
  groupId: string;
  participantId: string;
  deviceMemory: UseDeviceMemoryResult;
  onLeaveClick: () => void;
}

/**
 * The confirmed participant's page. It shows "Ready to draw?" until the draw animation has
 * revealed this participant's match on this device, regardless of reveals elsewhere.
 */
export const ParticipantDashboard = ({
  groupId,
  participantId,
  deviceMemory,
  onLeaveClick,
}: ParticipantDashboardProps) => {
  const { captureEvent, groupClient, wishlistClient } = useServices();
  const queryClient = useQueryClient();
  const getGroupForParticipant = useGetGroupForParticipant(groupClient, {
    groupId,
    participantId,
  });
  const recordMatchView = useRecordMatchView(groupClient, captureEvent);
  const [isDrawing, setIsDrawing] = useState(false);

  // A failed background refetch keeps showing the data already loaded.
  if (getGroupForParticipant.httpError && !getGroupForParticipant.result) {
    return <UnknownErrorPage />;
  }

  if (getGroupForParticipant.isPending) {
    return <LoadingPage />;
  }

  invariant(getGroupForParticipant.result, 'Group for participant result must be defined');

  if (!getGroupForParticipant.result.success) {
    switch (getGroupForParticipant.result.code) {
      case GetGroupForParticipantErrorCodes.INVALID_INPUT:
      case GetGroupForParticipantErrorCodes.NOT_FOUND:
        return <NotFoundPage />;
      default:
        throw new UnreachableError(getGroupForParticipant.result.code);
    }
  }

  if (getGroupForParticipant.result.kind === GetGroupSuccessKinds.DELETED) {
    return (
      <PageLayout>
        <DeletedGroupCard
          groupUrl={getGroupHref({ groupId, absolute: true })}
          createGroupHref={getCreateGroupHref()}
        />
      </PageLayout>
    );
  }

  const { group, participant, match, matchWishlist } = getGroupForParticipant.result;
  const isRevealed = DeviceMemoryRules.isLastRevealed(deviceMemory.memory, participant.id);

  const handleDrawClick = () => {
    captureEvent(GroupNameDrawClickEvent, {});
    recordMatchView.mutate(
      { groupId, participantId },
      {
        onSuccess: result => {
          if (result.success) setIsDrawing(true);
        },
      },
    );
  };

  const handleDrawDone = () => {
    setIsDrawing(false);
    recordMatchView.reset();
    // Refresh drawn progress and the confirmation's "chosen before" state.
    void queryClient.invalidateQueries({ queryKey: getGroupQueryKey(groupId) });
  };

  if (isDrawing) {
    return (
      <DashboardLayout>
        <DrawSlotMachine
          captureEvent={captureEvent}
          names={group.participants.filter(p => p.id !== participant.id).map(p => p.name)}
          matchName={match.name}
          onReveal={() => deviceMemory.markRevealed(participant.id)}
          onDrawDone={handleDrawDone}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <GroupHeaderCard groupName={group.name} participantName={participant.name} />

      {/* Only shown until the match is revealed on this device */}
      {!isRevealed && (
        <DrawCard
          onDrawClick={handleDrawClick}
          isLoading={recordMatchView.isPending}
          errorMessage={recordMatchView.displayErrorMessage}
        />
      )}

      {/* Only shown once the match is revealed on this device */}
      {isRevealed && (
        <>
          <MatchCard matchName={match.name} />
          <MatchWishlistCard
            captureEvent={captureEvent}
            matchName={match.name}
            items={matchWishlist}
          />
          <OwnWishlistCard
            captureEvent={captureEvent}
            wishlistClient={wishlistClient}
            groupId={groupId}
            participantId={participant.id}
          />
          <ProgressCard participants={group.participants} currentParticipantId={participant.id} />
        </>
      )}

      {/* Always shown to the user */}
      <HelpCard
        participantId={participant.id}
        participantName={participant.name}
        groupUrl={getGroupHref({ groupId, absolute: true })}
        createGroupHref={getCreateGroupHref()}
        onLeaveClick={() => {
          captureEvent(GroupNameResetClickEvent, {});
          onLeaveClick();
        }}
      />

      {/* Only shown once the match is revealed on this device */}
      {isRevealed && <AffiliateDisclosureCard />}
    </DashboardLayout>
  );
};

/**
 * Dashboard layout wrapper. Uses {@link PageLayout} for the gradient background
 * and stacks multiple cards in a centered column.
 */
const DashboardLayout = ({ children }: { children: ReactNode }) => {
  return (
    <PageLayout>
      <div data-testid="participant-dashboard-step" className="flex w-full flex-col gap-2">
        {children}
      </div>
    </PageLayout>
  );
};
